// components/use-case-list/use-case-filter.tsx
"use client";

import { ChevronDown, SearchIcon, SlidersHorizontal,X } from "lucide-react";
import React from "react";

import { FilterState } from "@/app/types/use-case-list";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";

interface UseCaseFilterProps {
  industryOptions: string[];
  modelOptions: string[];
  useCaseTypeOptions: string[];
  activeFilters: FilterState;
  setActiveFilters: (state: FilterState) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
}

export function UseCaseFilter({
  industryOptions,
  modelOptions,
  useCaseTypeOptions,
  activeFilters,
  setActiveFilters,
  searchQuery,
  setSearchQuery,
}: UseCaseFilterProps) {
  // Toggle a filter selection
  const toggleFilter = (type: keyof FilterState, value: string) => {
    setActiveFilters({
      ...activeFilters,
      [type]: activeFilters[type].includes(value)
        ? activeFilters[type].filter((item: string) => item !== value)
        : [...activeFilters[type], value],
    });
  };

  // Clear all filters
  const clearAllFilters = () => {
    setActiveFilters({
      industries: [],
      models: [],
      useCaseTypes: [],
    });
    setSearchQuery("");
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery("");
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row gap-6 items-start md:items-center justify-between">
        {/* Search - TALLER HEIGHT */}
        <div className="relative w-full md:w-auto md:flex-1 max-w-xl">
          <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
            <SearchIcon className="h-6 w-6 text-blue-500" />
          </div>
          <Input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Find AI use cases by industry, tech, or problem..."
            className="pl-14 pr-12 py-6 h-16 text-lg bg-gray-800/50 border-gray-700 rounded-md text-white w-full focus:border-blue-500 focus:ring-blue-500 placeholder-gray-400"
          />
          {searchQuery && (
            <button
              className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400 hover:text-white"
              onClick={clearSearch}
            >
              <X className="h-6 w-6" />
            </button>
          )}
        </div>

        {/* Filters using shadcn DropdownMenu */}
        <div className="flex flex-wrap gap-3 w-full md:w-auto">
          {/* Industry Filter */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="border-gray-700 h-12 text-white bg-gray-800/50 hover:bg-gray-700 hover:text-white flex items-center gap-2"
              >
                <SlidersHorizontal className="h-5 w-5 text-blue-500" />
                <span className="text-base">Industry</span>
                <ChevronDown className="h-4 w-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-gray-800 border-gray-700 text-white">
              {industryOptions.map((industry) => (
                <DropdownMenuCheckboxItem
                  key={industry}
                  checked={activeFilters.industries.includes(industry)}
                  onCheckedChange={() => toggleFilter("industries", industry)}
                  className="cursor-pointer hover:bg-gray-700 focus:bg-gray-700"
                >
                  {industry}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* AI Model Filter */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="border-gray-700 h-12 text-white bg-gray-800/50 hover:bg-gray-700 hover:text-white flex items-center gap-2"
              >
                <SlidersHorizontal className="h-5 w-5 text-blue-500" />
                <span className="text-base">AI Model</span>
                <ChevronDown className="h-4 w-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-gray-800 border-gray-700 text-white">
              {modelOptions.map((model) => (
                <DropdownMenuCheckboxItem
                  key={model}
                  checked={activeFilters.models.includes(model)}
                  onCheckedChange={() => toggleFilter("models", model)}
                  className="cursor-pointer hover:bg-gray-700 focus:bg-gray-700"
                >
                  {model}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Use Case Type Filter */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="border-gray-700 h-12 text-white bg-gray-800/50 hover:bg-gray-700 hover:text-white flex items-center gap-2"
              >
                <SlidersHorizontal className="h-5 w-5 text-blue-500" />
                <span className="text-base">Use Case Type</span>
                <ChevronDown className="h-4 w-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-gray-800 border-gray-700 text-white">
              {useCaseTypeOptions.map((type) => (
                <DropdownMenuCheckboxItem
                  key={type}
                  checked={activeFilters.useCaseTypes.includes(type)}
                  onCheckedChange={() => toggleFilter("useCaseTypes", type)}
                  className="cursor-pointer hover:bg-gray-700 focus:bg-gray-700"
                >
                  {type}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Active Filters */}
      {(activeFilters.industries.length > 0 ||
        activeFilters.models.length > 0 ||
        activeFilters.useCaseTypes.length > 0 ||
        searchQuery) && (
        <div className="mt-4 flex flex-wrap gap-2 items-center">
          <span className="text-sm text-gray-400">Active filters:</span>

          {searchQuery && (
            <Badge className="bg-gray-800 text-white flex items-center gap-1 px-3 py-1.5 border border-gray-700">
              Search: {searchQuery}
              <button
                className="ml-1 hover:text-blue-400"
                onClick={clearSearch}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}

          {activeFilters.industries.map((industry: string) => (
            <Badge
              key={`industry-${industry}`}
              className="bg-gray-800 text-white flex items-center gap-1 px-3 py-1.5 border border-gray-700"
            >
              {industry}
              <button
                className="ml-1 hover:text-blue-400"
                onClick={() => toggleFilter("industries", industry)}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}

          {activeFilters.models.map((model: string) => (
            <Badge
              key={`model-${model}`}
              className="bg-gray-800 text-white flex items-center gap-1 px-3 py-1.5 border border-gray-700"
            >
              {model}
              <button
                className="ml-1 hover:text-blue-400"
                onClick={() => toggleFilter("models", model)}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}

          {activeFilters.useCaseTypes.map((type: string) => (
            <Badge
              key={`type-${type}`}
              className="bg-gray-800 text-white flex items-center gap-1 px-3 py-1.5 border border-gray-700"
            >
              {type}
              <button
                className="ml-1 hover:text-blue-400"
                onClick={() => toggleFilter("useCaseTypes", type)}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}

          {(activeFilters.industries.length > 0 ||
            activeFilters.models.length > 0 ||
            activeFilters.useCaseTypes.length > 0 ||
            searchQuery) && (
            <Button
              variant="link"
              className="text-blue-500 hover:text-blue-400 p-0 h-auto text-sm font-medium"
              onClick={clearAllFilters}
            >
              Clear all filters
            </Button>
          )}
        </div>
      )}
    </div>
  );
} 